import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { MzicSvgComponent, SpinnerComponent } from '@mzic/mzic-components';
import { CollectionRevisionFileEditorController } from '../../../collection-flow/collection-revision-file-editor.controller';
import {
  CollectionRequiredInputDescriptor,
  CollectionRevisionFileEditorRow,
  CollectionRevisionNavigationDirection,
  isSupportedRequiredInputDescriptor,
} from '../../../collection-flow/collection-revision-file-editor';
import {
  CollectionReportFileCardAction,
  CollectionReportFileCardComponent,
  CollectionReportFileCardStatus,
} from '../../collection-report-file-card/collection-report-file-card.component';

type UploadContext = 'ecad' | 'streaming' | 'mechanical' | 'default';

@Component({
  selector: 'lib-file-upload-step',
  imports: [
    CommonModule,
    TranslocoModule,
    MzicSvgComponent,
    SpinnerComponent,
    CollectionReportFileCardComponent,
  ],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './file-upload-step.component.html',
  styleUrl: './file-upload-step.component.scss',
})
export class FileUploadStepComponent {
  private static readonly MAX_FILE_SIZE_BYTES = 256 * 1024 * 1024;
  private readonly editor = inject(CollectionRevisionFileEditorController);

  @Input() revenueType = '';
  @Input() revenueSubtype: string | null = null;
  @Input() integrationEnabled = false;
  @Input() integrationEntityName = '';
  @Input() isOwner = false;
  @Input() hasSharedTarget = false;
  @Input() skipButtonHidden = false;
  @Output() navigationRequested =
    new EventEmitter<CollectionRevisionNavigationDirection>();
  @Output() primaryActionRequested = new EventEmitter<void>();
  @Output() reviewRequiredInputsRequested = new EventEmitter<void>();
  @Output() retryUploadRequested = new EventEmitter<string>();
  @Output() share = new EventEmitter<void>();
  @Output() skip = new EventEmitter<void>();

  @ViewChild('backButton', { read: ElementRef })
  private backButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('primaryButton', { read: ElementRef })
  private primaryButton?: ElementRef<HTMLButtonElement>;

  readonly editorState = this.editor.state;
  readonly isDragOver = signal(false);
  readonly acceptedTypes = [
    '.pdf',
    '.xlsx',
    '.xls',
    '.csv',
    '.txt',
    '.tab',
    '.tsv',
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'text/plain',
    'text/tab-separated-values',
  ];
  private readonly acceptedExtensions = new Set([
    '.pdf',
    '.xlsx',
    '.xls',
    '.csv',
    '.txt',
    '.tab',
    '.tsv',
  ]);

  get uploadContext(): UploadContext {
    const type = this.revenueType.toUpperCase();
    const subtype = this.revenueSubtype?.toUpperCase();
    if (type === 'PUBLIC_PERFORMANCE' && subtype === 'EXECUTION') return 'ecad';
    if (type === 'STREAMING') return 'streaming';
    if (type === 'MECHANICAL' || subtype === 'MECHANICAL') return 'mechanical';
    return 'default';
  }

  get contextKeySuffix(): string {
    return `${this.uploadContext}${this.integrationEnabled ? '_integration' : ''}`;
  }

  get sharePromptKey(): string {
    return `lang.file_upload_share_prompt${
      this.integrationEnabled ? '_integration' : ''
    }`;
  }

  get isDisabled(): boolean {
    return this.editorState().busy;
  }

  get isReadOnly(): boolean {
    return !this.editorState().editable;
  }

  get isMutationDisabled(): boolean {
    return this.isDisabled || this.isReadOnly;
  }

  get canPerformPrimaryAction(): boolean {
    if (this.hasBlockingValidationIssues) {
      return !this.isDisabled;
    }
    return (
      !this.isDisabled &&
      this.editorState().primaryActionEnabled &&
      !this.hasBlockingValidationIssues
    );
  }

  get unresolvedRequiredReportCount(): number {
    return Object.values(this.editorState().requiredInputsByFile).filter(
      (requiredInputs) => requiredInputs.length > 0,
    ).length;
  }

  get validationAttentionReportCount(): number {
    const state = this.editorState();
    const fileIds = new Set<string>();

    Object.values(state.validationByFile).forEach((validation) => {
      if (!validation.usable || validation.required_inputs.length > 0) {
        fileIds.add(validation.file_id);
      }
    });
    Object.entries(state.requiredInputsByFile).forEach(
      ([fileId, requiredInputs]) => {
        if (requiredInputs.length > 0) fileIds.add(fileId);
      },
    );

    return fileIds.size;
  }

  get hasBlockingValidationIssues(): boolean {
    const state = this.editorState();
    const hasPermanentRejection = Object.values(state.validationByFile).some(
      (validation) =>
        !validation.usable && validation.required_inputs.length === 0,
    );
    const hasUnresolvedRequirement = Object.entries(
      state.requiredInputsByFile,
    ).some(([fileId, requiredInputs]) =>
      requiredInputs.some(
        (descriptor) =>
          !isSupportedRequiredInputDescriptor(descriptor) ||
          !state.resolvedInputsByFile[fileId]?.[descriptor.key]?.trim(),
      ),
    );
    return hasPermanentRejection || hasUnresolvedRequirement;
  }

  get requiredFilesCount(): number {
    return this.editorState().requiredFilesCount;
  }

  get failureAnnouncementKey(): string | null {
    const failure = this.editorState().failure;
    return failure && failure.kind !== 'UPLOAD' && failure.kind !== 'VALIDATION'
      ? `lang.file_upload_failure_${failure.kind.toLowerCase()}`
      : null;
  }

  get failureFileNames(): string {
    return this.editorState().failure?.fileNames.join(', ') ?? '';
  }

  get primaryActionLabelKey(): string {
    if (!this.isDisabled && this.hasBlockingValidationIssues) {
      return this.validationAttentionReportCount === 1
        ? 'lang.file_validation_review_report'
        : 'lang.file_validation_review_reports_count';
    }
    switch (this.editorState().phase) {
      case 'UPLOADING':
        return 'lang.file_upload_uploading';
      case 'VALIDATING':
        return 'lang.file_upload_validating';
      case 'UPDATING':
        return 'lang.file_upload_updating';
      case 'RELOADING':
        return 'lang.file_upload_reloading';
      default:
        return this.editorState().mode === 'INITIAL_SUBMISSION'
          ? 'lang.file_upload_upload'
          : 'lang.continue';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.isMutationDisabled) this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
    if (!this.isMutationDisabled && event.dataTransfer?.files) {
      this.addRevisionFiles(Array.from(event.dataTransfer.files));
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!this.isMutationDisabled && input.files) {
      this.addRevisionFiles(Array.from(input.files));
    }
    input.value = '';
  }

  addRevisionFiles(files: readonly File[]): boolean {
    if (this.isMutationDisabled) return false;
    const accepted = files.filter(
      (file) =>
        file.size <= FileUploadStepComponent.MAX_FILE_SIZE_BYTES &&
        this.acceptedExtensions.has(this.getFileExtension(file.name)),
    );
    return accepted.length > 0 && this.editor.addFiles(accepted);
  }

  removeRevisionFile(key: string): boolean {
    if (this.isMutationDisabled) return false;
    return this.editor.removeFile(key);
  }

  requestBack(): void {
    this.requestNavigation('BACK');
  }

  requestPrimaryAction(): void {
    if (!this.canPerformPrimaryAction) return;
    if (this.hasBlockingValidationIssues) {
      this.reviewRequiredInputsRequested.emit();
      return;
    }
    this.primaryActionRequested.emit();
  }

  focusNavigationControl(
    direction: CollectionRevisionNavigationDirection,
  ): void {
    const control = direction === 'BACK' ? this.backButton : this.primaryButton;
    control?.nativeElement.focus();
  }

  requestShare(): void {
    if (!this.isMutationDisabled) this.share.emit();
  }

  requestSkip(): void {
    if (!this.isMutationDisabled) this.skip.emit();
  }

  displayName(row: CollectionRevisionFileEditorRow): string {
    return (
      row.name || (row.origin === 'PERSISTED' ? row.fileId : row.file.name)
    );
  }

  requiredInputs(
    row: CollectionRevisionFileEditorRow,
  ): readonly CollectionRequiredInputDescriptor[] {
    const fileId = this.editor.fileIdForRow(row);
    return fileId
      ? (this.editorState().requiredInputsByFile[fileId] ?? [])
      : [];
  }

  getFileExtension(filename: string): string {
    const index = filename.lastIndexOf('.');
    return index < 0 ? '' : filename.slice(index).toLowerCase();
  }

  cardStatus(
    row: CollectionRevisionFileEditorRow,
  ): CollectionReportFileCardStatus {
    if (row.origin !== 'LOCAL') return 'DEFAULT';
    const state = this.editorState().uploadStateByClientId[row.clientId];
    return state === 'UPLOAD_FAILED'
      ? 'UPLOAD_FAILED'
      : state === 'UPLOADING'
        ? 'UPLOADING'
        : 'DEFAULT';
  }

  cardAction(
    row: CollectionRevisionFileEditorRow,
  ): CollectionReportFileCardAction {
    return row.origin === 'LOCAL' && this.cardStatus(row) === 'UPLOAD_FAILED'
      ? 'RETRY'
      : 'REMOVE';
  }

  retryRevisionFile(row: CollectionRevisionFileEditorRow): void {
    if (
      row.origin === 'LOCAL' &&
      this.cardStatus(row) === 'UPLOAD_FAILED' &&
      !this.isDisabled
    ) {
      this.retryUploadRequested.emit(row.clientId);
    }
  }

  private requestNavigation(
    direction: CollectionRevisionNavigationDirection,
  ): void {
    if (!this.isDisabled) this.navigationRequested.emit(direction);
  }
}
